/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/suv/computeRoiSuvForPairs.js
 *
 * Recomputes SUV histograms + stats for specific (ROINumber, ctSeriesUID)
 * pairs only. Bridge entries for unaffected ROIs are left untouched.
 *
 * Used by recomputeOnEdit.js when a polygon contour is edited — only the
 * affected (ROI, timepoint) needs voxel re-sampling, not all N × T pairs.
 * Liver-reference cascade still falls out naturally: this recomputes the
 * liver's histogram, then computePsmaPosVolumesFromBridge (called after)
 * re-derives the soft-tissue threshold and re-queries every lesion histogram.
 *
 * @param {object} args
 * @param {Array<{ROINumber: number, ctSeriesUID: string}>} args.affectedPairs
 * @param {Array}    args.imageSets
 * @param {Function} args.getStructureSet  uid => structureSet object
 */

import {
  discoverTimepointPairs,
  extractContoursByCtSlice,
  computePetVoxelVolumeMm3,
  makeBridgeEntry,
  makeEmptyEntry,
} from './computeAllRoiSuvAcrossTimepoints';
import {
  computeRoiSuv,
  buildCtToPetImageIdMap,
} from '../../../../../../cornerstone/src/modules/suv/computeRoiSuv';
import {
  makeSuvKey,
  setSuvByKey,
  startComputing,
  stopComputing,
} from './suvViewportBridge';

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[SUV-COMPUTE-SCOPED]', ...args);
};

export default async function computeRoiSuvForPairs({
  affectedPairs,
  imageSets,
  getStructureSet,
}) {
  if (!affectedPairs?.length) return;
  log('affectedPairs', affectedPairs);

  const tpPairs = discoverTimepointPairs(imageSets);

  startComputing();
  let processed = 0;
  try {
    for (const tpPair of tpPairs) {
      const { structureSetUID, ctSeriesUID, ctImageIds, petImageIds } = tpPair;

      // Only process ROIs that appear in affectedPairs for this ctSeriesUID
      const affectedRoiNumbers = affectedPairs
        .filter(p => p.ctSeriesUID === ctSeriesUID)
        .map(p => p.ROINumber);
      if (!affectedRoiNumbers.length) continue;

      const ss = getStructureSet(structureSetUID);
      const rois = (ss?.ROIContours || []).filter(r =>
        affectedRoiNumbers.includes(r.ROINumber)
      );
      if (!rois.length) continue;

      const petVoxelVolumeMm3 = computePetVoxelVolumeMm3(petImageIds);
      const ctToPetMap = buildCtToPetImageIdMap(ctImageIds, petImageIds);

      for (const roi of rois) {
        const key = makeSuvKey(structureSetUID, roi.ROINumber, ctSeriesUID);
        const contoursByCtSlice = extractContoursByCtSlice(
          structureSetUID,
          roi.ROINumber,
          ctImageIds
        );
        if (!contoursByCtSlice.length) {
          setSuvByKey(
            key,
            makeEmptyEntry('no-contours-on-fused-ct', petVoxelVolumeMm3)
          );
          processed++;
          continue;
        }
        try {
          const result = await computeRoiSuv({
            contoursByCtSlice,
            ctToPetImageIdMap: ctToPetMap,
            petVoxelVolumeMm3,
          });
          setSuvByKey(key, makeBridgeEntry(result));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[SUV-COMPUTE-SCOPED] ROI failed', { key, err });
          setSuvByKey(key, makeEmptyEntry('compute-error', petVoxelVolumeMm3));
        }
        processed++;
      }
    }
    log(
      'done — processed',
      processed,
      'of',
      affectedPairs.length,
      'requested pairs'
    );
  } finally {
    stopComputing();
  }
}
